import React, { useState, useEffect } from 'react';
import { useSxStyles } from '../styles/sxStyles';
import { getCurrency } from '../config/utils';
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid2 from '@mui/material/Grid2';
import FormControl from '@mui/material/FormControl';
import FormLabel from "@mui/material/FormLabel";
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { PatternFormat } from "react-number-format";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {isDate, isValid, endOfMonth, startOfToday, isAfter, format} from 'date-fns';
import { Visa, Mastercard, Discover, Amex } from 'react-pay-icons';
import PaymentLocation from "./PaymentLocation";

const CardData = (props) => {
    const showTitle = props.showTitle;
    const paymentRequest = props.paymentRequest;
    const paymentFields = props.paymentFields;
    const setPaymentFields = props.setPaymentFields;
    const locations = props.locations;
    const user = props.user;
    const setLocation = props.setLocation;
    const court = props.court;
    const classes = useSxStyles;
    const [expDate, setExpDate] = useState(endOfMonth(new Date()));
    const [cardNo, setCardNo] = useState(null);
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const CardIcon = () => {
        switch (cardNo) {
            case '3':
                return (
                    <Amex style={{width: '35px'}} />
                );
                break;
            case '4':
                return (
                    <Visa style={{width: '35px'}} />
                );
                break;
            case '5':
                return (
                    <Mastercard style={{width: '35px'}} />
                );
                break;
            case '6':
                return (
                    <Discover style={{width: '35px'}} />
                );
                break;
            default:
                return null;
        }
    }

    const handleCardNumber = (values) => {
        const val = values.value;
        if (val && val.length > 0) {
            setCardNo(val.substring(0,1));
        }
        setPaymentFields({...paymentFields, cardNumber: val});
    }

    const handleExp = (value) => {
        const val = value;
        const testDate = startOfToday();
        if (!val) {
            setExpDate(null);
        } else if (isDate(val) && isValid(val)) {
            const exp = endOfMonth(val);
            if (isAfter(exp, testDate)) {
                setExpDate(exp);
            } else {
                setExpDate(null);
            }
        } else {
            setExpDate(null);
        }
    };

    const handleCvv2 = (values) => {
        const val = values.value;
        setPaymentFields({...paymentFields, cvv2: val});
    }

    const handleName = (e) => {
        const val = e.target.value;
        if (val && val !== paymentRequest.payorName) {
            setPaymentFields({...paymentFields, payorName: val});
        }
    }

    const handleAddress = (e) => {
        const val = e.target.value;
        if (val && val !== paymentRequest.payorAddress) {
            setPaymentFields({...paymentFields, payorAddress: val});
        }
    }

    const handleZip = (values) => {
        if (values && values.value && values.value.length === 5) {
            const val = values.value;
            if (val !== paymentRequest.billingZip) {
                setPaymentFields({...paymentFields, billingZip: val});
            }
        }
    }

    const handleEmail = (e) => {
        const val = e.target.value;
        if (val && val !== paymentRequest.email) {
            setPaymentFields({...paymentFields, email: val});
        }
    }

    const handlePhone = (values) => {
        if (values && values.value && values.value.length === 10) {
            const val = values.formattedValue;
            if (val !== paymentRequest.phone) {
                setPaymentFields({...paymentFields, phone: val});
            }
        }
    }

    useEffect(() => {
        if (expDate && isDate(expDate) && isValid(expDate)) {
            setPaymentFields({
                ...paymentFields,
                expMonth: format(expDate, 'MM'),
                expYear: format(expDate, 'yy'),
                expDate: expDate
            });
        } else {
            setPaymentFields({...paymentFields, expMonth: null, expYear: null});
        }
    }, [expDate])

    return (<>
        {showTitle &&
            <Box sx={{ borderBottom: 1, borderColor: 'divider', paddingBottom: '1rem', marginBottom: '1rem' }}>
                <Typography component='div' variant='body1' color='primary'>CARD PAYMENT</Typography>
            </Box>
        }
        <Paper variant='body1' elevation={3} sx={classes.controlPanel}>
            <Grid2 container spacing={1}>
                <Grid2
                    sx={{paddingLeft: '0', paddingTop: '1rem'}}
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <Typography component='span' variant='subtitle1' color='primary' sx={{fontSize: '1.2rem'}}>
                        Amount:
                    </Typography>
                    <Typography component='span' variant='subtitle2' color='textPrimary' sx={{fontSize: '1.2rem', marginLeft: '1rem'}}>
                        {getCurrency(paymentRequest.totalAmount)}
                    </Typography>
                </Grid2>
                <Grid2
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <FormControl fullWidth sx={[smallScreen ? {
                        marginLeft: '0'
                    } : {
                        marginLeft: '.5rem'
                    }]}>
                        <FormLabel shrink={true} required sx={{...classes.inputLabel, paddingTop: '0', marginTop: '0'}}>
                            Name on Card
                        </FormLabel>
                        <TextField
                            name='name'
                            variant='outlined'
                            fullWidth
                            autoComplete='off'
                            defaultValue={paymentRequest.payorName}
                            onBlur={handleName}
                            slotProps={{
                                htmlInput: {sx: classes.textbox}
                            }}
                        />
                    </FormControl>
                </Grid2>
                <Grid2
                    sx={{paddingLeft: '0'}}
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <FormControl fullWidth sx={{marginLeft: '0'}}>
                        <FormLabel htmlFor='cardNumber' required shrink={true} sx={{...classes.inputLabel}}>Card Number</FormLabel>
                        <PatternFormat
                            name='cardNumber'
                            customInput={TextField}
                            InputProps={{
                                id: 'cardNumber',
                                sx: {...classes.numberbox},
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <CardIcon />
                                    </InputAdornment>
                                )
                            }}
                            type='text'
                            autoComplete='off'
                            variant='outlined'
                            format={cardNo === '3' ? '#### ###### #####' : '#### #### #### ####'}
                            onValueChange={(values) => handleCardNumber(values)}
                        />
                    </FormControl>
                </Grid2>
                <Grid2
                    sx={{paddingTop: '0'}}
                    size={{
                        xs: 6,
                        sm: 3
                    }}>
                    <FormControl fullWidth sx={[smallScreen ? {
                        marginLeft: '0'
                    } : {
                        marginLeft: '.5rem'
                    }]}>
                        <FormLabel htmlFor='cardExp' required shrink={true} sx={classes.inputLabel}>Expires</FormLabel>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                name='cardExp'
                                views={['month', 'year']}
                                onChange={(newValue) => handleExp(newValue)}
                                value={expDate}
                                format='MM/yy'
                                disableMaskedInput={false}
                                slotProps={{
                                    textField: {
                                        inputProps: {
                                            sx: classes.textbox
                                        }
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </FormControl>
                </Grid2>
                <Grid2
                    size={{
                        xs: 6,
                        sm: 3
                    }}>
                    <FormControl fullWidth sx={{marginLeft: '.5rem'}}>
                        <FormLabel required htmlFor='cvv2' shrink={true} sx={classes.inputLabel}>CVC</FormLabel>
                        <PatternFormat
                            id='cvv2'
                            name='cvv2'
                            customInput={TextField}
                            type='text'
                            autoComplete='off'
                            variant='outlined'
                            format={cardNo === '3' ? '####' : '###'}
                            InputProps={{
                                sx: classes.numberbox,
                            }}
                            onValueChange={(values) => handleCvv2(values)}
                        />
                    </FormControl>
                </Grid2>
                <Grid2
                    sx={{paddingLeft: '0', marginTop: '.5rem'}}
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <FormControl fullWidth >
                        <FormLabel shrink={true} sx={classes.inputLabel}>Billing Address</FormLabel>
                        <TextField
                            name='address'
                            variant='outlined'
                            fullWidth
                            defaultValue={paymentRequest.payorAddress}
                            onBlur={handleAddress}
                            slotProps={{
                                htmlInput: {sx: classes.textbox}
                            }}
                        />
                    </FormControl>
                </Grid2>
                <Grid2
                    sx={{marginTop: '.5rem'}}
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <FormControl fullWidth sx={[smallScreen ? {
                        marginLeft: '0'
                    } : {
                        marginLeft: '.5rem'
                    }]}>
                        <FormLabel htmlFor='billigZip' shrink={true} required={!!court.requireZip} sx={classes.inputLabel} >Billing Zip</FormLabel>
                        <PatternFormat
                            id='billingZip'
                            name='billingZip'
                            customInput={TextField}
                            type='text'
                            autoComplete='off'
                            variant='outlined'
                            format='#####'
                            InputProps={{
                                sx: {...classes.numberbox}
                            }}
                            defaultValue={paymentRequest.billingZip}
                            onValueChange={(values) => handleZip(values)}
                        />
                    </FormControl>
                </Grid2>
                <Grid2
                    sx={{paddingLeft: '0', marginTop: '.5rem'}}
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <FormControl fullWidth>
                        <FormLabel shrink={true} sx={classes.inputLabel}>Email Address</FormLabel>
                        <TextField
                            name='email'
                            variant='outlined'
                            fullWidth
                            defaultValue={paymentRequest.email}
                            onBlur={handleEmail}
                            slotProps={{
                                htmlInput: {sx: classes.textbox}
                            }}
                        />
                    </FormControl>
                </Grid2>
                <Grid2
                    sx={{marginTop: '.5rem'}}
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <FormControl fullWidth sx={[smallScreen ? {
                        marginLeft: '0'
                    } : {
                        marginLeft: '.5rem'
                    }]}>
                        <FormLabel shrink={true} sx={classes.inputLabel}>Phone Number</FormLabel>
                        <PatternFormat
                            name='phone'
                            customInput={TextField}
                            type='text'
                            autoComplete='off'
                            variant='outlined'
                            format="(###) ###-####"
                            InputProps={{
                                sx: {...classes.numberbox}
                            }}
                            defaultValue={paymentRequest.phone}
                            onValueChange={(values) => handlePhone(values)}
                        />
                    </FormControl>
                </Grid2>
                { locations && locations.length > 0 &&
                    <Grid2
                        style={{marginTop: '.5rem', paddingLeft: '0'}}
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <PaymentLocation locations={locations} user={user} setLocation={setLocation} />
                    </Grid2>
                }
            </Grid2>
        </Paper>
    </>);
}

export default CardData;
